import { useEffect, useRef, useState, type ReactElement } from 'react';
import { resolveOAuthCallbackSession } from '../../lib/auth-callback-exchange';
import {
  AUTH_INTENDED_URL_KEY,
  CANONICAL_DEV_ORIGIN,
  formatAuthErrorMessage,
} from '../../lib/auth-utils';
import { getSupabaseClient } from '../../lib/supabase';

function getIntendedRedirectPath(): string {
  let target = '/';
  try {
    const intended = sessionStorage.getItem(AUTH_INTENDED_URL_KEY);
    sessionStorage.removeItem(AUTH_INTENDED_URL_KEY);
    if (intended && intended.startsWith('/')) {
      target = intended;
    }
  } catch {
    /* ignore */
  }
  return target;
}

/** Handles Supabase OAuth PKCE return at /auth/callback. */
export function AuthCallbackPage(): ReactElement {
  const [message, setMessage] = useState('Signing you in…');
  const [isError, setIsError] = useState(false);
  const exchangeStartedRef = useRef(false);

  useEffect(() => {
    if (exchangeStartedRef.current) {
      return;
    }
    exchangeStartedRef.current = true;

    void (async () => {
      const supabase = getSupabaseClient();
      if (!supabase) {
        setIsError(true);
        setMessage('Supabase is not configured.');
        return;
      }

      let session;
      try {
        session = await resolveOAuthCallbackSession();
      } catch (exchangeError: unknown) {
        const errorMessage =
          exchangeError instanceof Error ? exchangeError.message : 'OAuth sign-in failed';
        setIsError(true);
        setMessage(formatAuthErrorMessage(errorMessage));
        return;
      }

      if (!session) {
        setIsError(true);
        setMessage(
          'No session after redirect. Add this URL to Supabase Auth redirect allowlist.',
        );
        return;
      }

      const target = getIntendedRedirectPath();
      const onLegacyDevHost =
        import.meta.env.DEV &&
        (window.location.hostname === '127.0.0.1' || window.location.hostname === '[::1]');

      if (onLegacyDevHost) {
        window.location.replace(`${CANONICAL_DEV_ORIGIN}${target}`);
        return;
      }

      window.history.replaceState({}, '', target);
      window.dispatchEvent(new PopStateEvent('popstate'));
    })();
  }, []);

  return (
    <div
      className={`vibe-auth-callback${isError ? ' vibe-auth-callback--error' : ''}`}
      data-testid="auth-callback"
    >
      {message}
    </div>
  );
}
